import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { LogOut, ShieldCheck } from "lucide-react";
import { authClient } from "../lib/auth-client";
import { Button, Input, Label, toastManager } from "../components/ui";
import { SettingsHeader } from "../components/SettingsHeader";
import { SESSION_KEY, useSessionUser } from "../lib/useSessionUser";
import { initialsFor } from "@/lib/utils";

export const Route = createFileRoute("/settings/profile")({
  component: ProfileSettings,
});

const NAME_MAX = 80;

function ProfileSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSessionUser();
  const user = session.data?.user;
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setName(user.name ?? "");
  }, [user]);

  const trimmed = name.trim();
  const dirty = user !== undefined && trimmed !== (user.name ?? "");

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty || !trimmed) return;
    setSaving(true);
    const { error } = await authClient.updateUser({ name: trimmed });
    setSaving(false);
    if (error) {
      toastManager.add({ type: "error", title: "Could not save your name" });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: SESSION_KEY });
    toastManager.add({ type: "success", title: "Name saved" });
  }

  async function signOut() {
    await authClient.signOut();
    queryClient.removeQueries({ queryKey: SESSION_KEY });
    void navigate({ to: "/sign-in" });
  }

  return (
    <div className="space-y-6">
      <SettingsHeader title="Profile">
        How you appear in SVG Mascot and in our emails.
      </SettingsHeader>

      <section className="tactile p-4 sm:p-6">
        <div className="flex items-center gap-4">
          <Avatar image={user?.image} email={user?.email ?? null} />
          <div className="min-w-0">
            <p className="text-xs font-extrabold uppercase tracking-wider text-ink-faint">
              Signed in as
            </p>
            <p className="truncate text-base font-bold text-ink">
              {user?.email ?? "—"}
            </p>
            <p className="text-xs font-semibold text-ink-soft">
              Your email and photo come from the account you sign in with.
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => void saveName(e)}
          className="mt-6 border-t-2 border-line pt-5"
        >
          <Label htmlFor="profile-name">Name</Label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <Input
              id="profile-name"
              value={name}
              maxLength={NAME_MAX}
              autoComplete="name"
              disabled={session.isLoading}
              onChange={(e) => setName(e.target.value)}
              className="sm:max-w-sm"
            />
            <Button
              type="submit"
              loading={saving}
              disabled={!dirty || !trimmed}
            >
              Save
            </Button>
          </div>
          <p className="mt-2 text-xs font-semibold text-ink-soft">
            We greet you with it in emails.
          </p>
        </form>
      </section>

      <section className="tactile flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div>
          <h3 className="text-base text-ink">Sign out</h3>
          <p className="mt-1 text-sm font-semibold text-ink-soft">
            Signs out of this browser. To sign out other devices, use Security.
          </p>
        </div>
        <Button
          variant="outline"
          className="shrink-0"
          onClick={() => void signOut()}
        >
          <LogOut /> Sign out
        </Button>
      </section>

      <section className="flex items-start gap-3 rounded-2xl border-2 border-dashed border-line-strong p-4 sm:p-5">
        <ShieldCheck
          className="mt-0.5 size-5 shrink-0 text-success"
          aria-hidden="true"
        />
        <div>
          <h3 className="text-base text-ink">Royalty-free ownership</h3>
          <p className="mt-1 text-sm font-semibold text-ink-soft">
            All mascot packs are delivered with full ownership. Commercial use,
            resale, modification, and redistribution are all permitted. No
            per-use fees, no attribution required.
          </p>
        </div>
      </section>
    </div>
  );
}

function Avatar({
  image,
  email,
}: {
  image: string | null | undefined;
  email: string | null;
}) {
  const [broken, setBroken] = useState(false);
  if (image && !broken) {
    return (
      <img
        src={image}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className="size-12 shrink-0 rounded-2xl border-2 border-line-strong object-cover sm:size-14"
      />
    );
  }
  return (
    <span className="flex size-12 shrink-0 items-center justify-center raised face-lilac-200 edge-lilac-500 depth-3 rounded-2xl font-display text-lg font-semibold text-ink sm:size-14">
      {initialsFor(email)}
    </span>
  );
}
